'use client'

/**
 * Purpose: Barcode + label capture composer for product scan (rear camera). Draws the placement
 * frame, snaps it onto a detected barcode, turns it green with a checkmark on a good read, and —
 * with `fullscreen` — takes over the whole phone screen.
 * Inputs: BarcodeCaptureComposerProps (callbacks, `fullscreen`, `onClose`, `unsupportedMessage`).
 * Outputs: onBarcodeDecoded (after the detected → read animation) / onLabelCaptured /
 * onUnsupportedCode / onScanAgain / onClose callbacks.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import IconButton from '@components/buttons/IconButton/IconButton'
import HoverGradientButton from '@components/buttons/HoverGradientButton/HoverGradientButton'
import type { BarcodeCaptureComposerProps } from '@lib/models/interfaces'
import { cameraFailureReason, type CameraFailureReason } from '@lib/camera-errors'
import { formatGtinForDisplay, normalizeGtin } from '@lib/gtin'
import { mapBoxToContainer, type NormalizedBox, type PixelRect } from '@lib/barcode-frame'
import { useVideoFrameCapture } from '@/hooks/use-video-frame-capture'
import { useBarcodeScanner } from '@/hooks/use-barcode-scanner'
import { CameraPreview } from '@components/captures/Camera/CameraPreview'
import './BarcodeCaptureComposer.css'

type CameraState = 'idle' | 'active' | 'error'
// What the viewfinder frame is showing: waiting, snapped onto a code, or a confirmed read.
type ScanPhase = 'searching' | 'detected' | 'read'

// Beat on the orange "found" frame, then on the green checkmark, before the lookup starts.
const DETECTED_HOLD_MS = 450
const READ_HOLD_MS = 900

// Guide frame (percent of the screen) shown until a barcode is detected.
const GUIDE_FRAME = { left: '11%', top: '38%', width: '78%', height: '26%' }

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

// Map getUserMedia failure reasons to scanPage.camera translation keys.
function cameraErrorKey(reason: CameraFailureReason): string {
  switch (reason) {
    case 'camera_in_use':
      return 'cameraInUse'
    case 'permission_denied':
      return 'cameraDenied'
    case 'no_camera':
      return 'noCamera'
    case 'constraints_unsatisfied':
      return 'constraintsUnsatisfied'
    // Fallback for unknown getUserMedia / hardware errors.
    default:
      return 'cameraError'
  }
}

// Rear-camera composer: ZXing barcode decode plus optional label frame capture.
export function BarcodeCaptureComposer({
  id = 'barcode-capture-composer',
  facingMode = 'environment',
  error: externalError,
  onError,
  disabled,
  className,
  onBarcodeDecoded,
  onUnsupportedCode,
  onLabelCaptured,
  decodePaused = false,
  overlay,
  labelScanning = false,
  onActiveChange,
  captureLabelRef,
  fullscreen = false,
  onClose,
  unsupportedMessage,
  onScanAgain,
}: BarcodeCaptureComposerProps) {
  const t = useTranslations('scanPage.camera')
  // videoRef feeds ZXing; cameraState drives idle / active / error overlays.
  const videoRef = useRef<HTMLVideoElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // Frozen copy of the frame the code was read from; stays visible after the stream stops.
  const freezeRef = useRef<HTMLCanvasElement>(null)
  const mountedRef = useRef(true)
  const [phase, setPhase] = useState<ScanPhase>('searching')
  const [frameRect, setFrameRect] = useState<PixelRect | null>(null)
  const [freezeVisible, setFreezeVisible] = useState(false)
  const [readCode, setReadCode] = useState<string | null>(null)
  const [cameraState, setCameraState] = useState<CameraState>('idle')
  const [internalError, setInternalError] = useState<string | null>(null)
  const displayError = externalError ?? internalError

  const { captureFrame } = useVideoFrameCapture()

  // useBarcodeScanner's onDecode (handleDecode) and its own resetDecodeGuard are mutually
  // dependent — the hook needs the callback, the callback needs the hook's resume function.
  // A ref breaks the cycle: handleDecode reads it at call time, long after this render set it.
  const resetDecodeGuardRef = useRef<() => void>(() => {})

  // Runs the moment ZXing reads a code, before the stream stops: freeze the frame and snap the
  // highlight onto the barcode's real position.
  const handleDetect = useCallback((box: NormalizedBox | null) => {
    const video = videoRef.current
    const root = rootRef.current
    const canvas = freezeRef.current
    if (!video || !root) return
    if (canvas && video.videoWidth > 0) {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d')?.drawImage(video, 0, 0)
      setFreezeVisible(true)
    }
    setFrameRect(
      box
        ? mapBoxToContainer(box, video.videoWidth, video.videoHeight, root.clientWidth, root.clientHeight)
        : null,
    )
    setPhase('detected')
  }, [])

  // Forward a decoded barcode to the parent scan flow after showing the confirmed read — unless
  // the client can already tell it's unsupported, in which case nothing is sent to the server at
  // all (design.md §3). The reader is already paused by the time this fires (its own decode
  // guard); the retry it hands the parent resumes it.
  const handleDecode = useCallback(
    async (barcode: string, symbology: Parameters<BarcodeCaptureComposerProps['onBarcodeDecoded']>[1]) => {
      const gtin = symbology === 'unsupported' ? null : normalizeGtin(barcode, symbology)
      if (!gtin) {
        onUnsupportedCode?.(() => resetDecodeGuardRef.current())
        return
      }
      // Orange "found" beat, then the green checkmark, then hand over to the lookup.
      await sleep(DETECTED_HOLD_MS)
      if (!mountedRef.current) return
      setReadCode(formatGtinForDisplay(gtin))
      setPhase('read')
      await sleep(READ_HOLD_MS)
      if (!mountedRef.current) return
      setCameraState('idle')
      await onBarcodeDecoded(barcode, symbology)
      // The parent has taken over; when the scanner comes back it starts from the plain guide.
      if (!mountedRef.current) return
      setPhase('searching')
      setFrameRect(null)
      setFreezeVisible(false)
      setReadCode(null)
    },
    [onBarcodeDecoded, onUnsupportedCode],
  )

  // ZXing reader hooks videoRef; exposes torch and decode-pause controls.
  const {
    start,
    stop,
    hasTorch,
    torchOn,
    toggleTorch,
    blockDecode,
    resetDecodeGuard,
  } = useBarcodeScanner({
    videoRef,
    onDecode: handleDecode,
    onDetect: handleDetect,
    decodePaused,
    facingMode,
  })
  // Refs must not be written during render — commit it in an effect instead. handleDecode is
  // only ever called later, from an async ZXing decode event, long after this effect has run.
  useEffect(() => {
    resetDecodeGuardRef.current = resetDecodeGuard
  }, [resetDecodeGuard])

  // Stop the camera stream when the composer unmounts, and cancel any pending read animation.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stop()
    }
  }, [stop])

  // Request camera permission and surface translated errors to parent.
  const startCamera = useCallback(async () => {
    if (disabled || decodePaused) return
    setInternalError(null)
    // A fresh start goes back to the empty guide frame over a live preview.
    setPhase('searching')
    setFrameRect(null)
    setFreezeVisible(false)
    setReadCode(null)
    resetDecodeGuard()
    try {
      await start()
      setCameraState('active')
    } catch (err) {
      const reason = cameraFailureReason(err)
      const msg = t(cameraErrorKey(reason))
      setInternalError(msg)
      onError?.(msg)
      // Surface error overlay so the user can retry allow-camera.
      setCameraState('error')
    }
  }, [decodePaused, disabled, onError, resetDecodeGuard, start, t])

  // Freeze a label frame, stop the stream, and hand base64 to OCR path.
  const captureLabel = useCallback(async () => {
    if (!videoRef.current || labelScanning) return
    blockDecode()
    const base64 = await captureFrame(videoRef.current)
    if (!base64) {
      const msg = t('frameCaptureError')
      setInternalError(msg)
      onError?.(msg)
      resetDecodeGuard()
      return
    }
    stop()
    setCameraState('idle')
    // Parent ScanTab runs OCR on the frozen label frame.
    onLabelCaptured(base64)
  }, [blockDecode, captureFrame, labelScanning, onError, onLabelCaptured, resetDecodeGuard, stop, t])

  // Unsupported-code retry: clear the message in the parent, then bring the camera back.
  const handleScanAgain = useCallback(() => {
    onScanAgain?.()
    void startCamera()
  }, [onScanAgain, startCamera])

  // Fullscreen close: release the camera first so it is off while the page is shown again.
  const handleClose = useCallback(() => {
    stop()
    setCameraState('idle')
    setPhase('searching')
    setFreezeVisible(false)
    onClose?.()
  }, [onClose, stop])

  useEffect(() => {
    onActiveChange?.(cameraState === 'active')
  }, [cameraState, onActiveChange])

  useEffect(() => {
    if (captureLabelRef) {
      captureLabelRef.current = () => void captureLabel()
    }
  }, [captureLabel, captureLabelRef])

  // Frame geometry: the guide until a code is detected, then the code's own box.
  const isUnsupported = !!unsupportedMessage
  const framePhase = isUnsupported ? 'unsupported' : phase
  const frameStyle =
    phase !== 'searching' && frameRect
      ? { left: frameRect.left, top: frameRect.top, width: frameRect.width, height: frameRect.height }
      : GUIDE_FRAME
  // The frame overlay shows while the camera runs and through the read/unsupported hold.
  const showFrame = cameraState === 'active' || phase !== 'searching' || isUnsupported
  const hint =
    framePhase === 'read'
      ? t('barcodeRead')
      : framePhase === 'detected'
        ? t('barcodeFound')
        : framePhase === 'searching'
          ? t('placeInFrame')
          : null

  // Idle overlay, live controls, and error state share one preview surface.
  return (
    <div
      id={id}
      ref={rootRef}
      className={`barcode-capture-composer ${fullscreen ? 'barcode-capture-composer--fullscreen' : ''} ${className ?? ''}`}
      data-phase={framePhase}
    >
      <CameraPreview
        videoRef={videoRef}
        className="camera-preview--fill"
        videoClassName="barcode-capture-composer__video"
        overlay={overlay}
      />
      {/* Still of the frame the code was read from, so the highlight has an image under it. */}
      <canvas
        id={`${id}-freeze`}
        ref={freezeRef}
        aria-hidden="true"
        className={`barcode-capture-composer__freeze ${freezeVisible ? '' : 'hidden'}`}
      />

      {showFrame && (
        <div id={`${id}-frame`} className="barcode-capture-composer__frame" style={frameStyle} aria-hidden="true">
          <span id={`${id}-frame-edge`} className="barcode-capture-composer__frame-edge" />
          <span id={`${id}-corner-tl`} className="barcode-capture-composer__corner barcode-capture-composer__corner--tl" />
          <span id={`${id}-corner-tr`} className="barcode-capture-composer__corner barcode-capture-composer__corner--tr" />
          <span id={`${id}-corner-bl`} className="barcode-capture-composer__corner barcode-capture-composer__corner--bl" />
          <span id={`${id}-corner-br`} className="barcode-capture-composer__corner barcode-capture-composer__corner--br" />
          {framePhase === 'searching' && <span id={`${id}-scan-line`} className="barcode-capture-composer__scan-line" />}
        </div>
      )}

      {/* Hint above the frame; polite live region so screen readers hear each state change. */}
      <div id={`${id}-hint`} className="barcode-capture-composer__hint" role="status" aria-live="polite">
        {showFrame && hint && <span id={`${id}-hint-text`} className="barcode-capture-composer__pill">{hint}</span>}
        {showFrame && framePhase === 'read' && readCode && /^\d+$/.test(readCode) && (
          <span id={`${id}-hint-code`} className="barcode-capture-composer__code">{readCode}</span>
        )}
      </div>

      {/* Green checkmark that confirms a successful read. */}
      {framePhase === 'read' && (
        <div id={`${id}-check`} className="barcode-capture-composer__check" aria-hidden="true">
          <svg id={`${id}-check-glyph`} width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21l8 8 14-17" />
          </svg>
        </div>
      )}

      {/* Fullscreen top bar: close on the left, torch on the right once the camera runs. */}
      {fullscreen && (
        <div id={`${id}-topbar`} className="barcode-capture-composer__topbar">
          {onClose ? (
            <IconButton
              id={`${id}-close`}
              ariaLabel={t('closeScanner')}
              onClick={handleClose}
              className="barcode-capture-composer__round"
            >
              <svg id={`${id}-close-glyph`} width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </IconButton>
          ) : <span id={`${id}-close-spacer`} />}
          {cameraState === 'active' && hasTorch && (
            <IconButton
              id={`${id}-torch-top`}
              ariaLabel={t('torchAriaLabel')}
              onClick={() => void toggleTorch()}
              className={`barcode-capture-composer__round ${torchOn ? 'barcode-capture-composer__torch-button--on' : ''}`}
            >
              ⚡
            </IconButton>
          )}
        </div>
      )}

      {/* Permission gate — shown before the rear camera stream starts. */}
      {cameraState === 'idle' && !decodePaused && phase === 'searching' && !isUnsupported && (
        <div id={`${id}-allow-overlay`} className="barcode-capture-composer__allow-overlay">
          <HoverGradientButton
            id={`${id}-allow-camera`}
            onClick={() => void startCamera()}
            className="barcode-capture-composer__allow-pill"
          >
            <svg width="13" height="12" viewBox="0 0 13 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M1.5 4V3a.75.75 0 0 1 .75-.75H3.5l1-1.5h4l1 1.5h1.25A.75.75 0 0 1 11.5 3v7.5a.75.75 0 0 1-.75.75H2.25a.75.75 0 0 1-.75-.75V4z" />
              <circle cx="6.5" cy="6.5" r="1.75" />
            </svg>
            {t('allowCamera')}
          </HoverGradientButton>
        </div>
      )}

      {/* Translated getUserMedia failure from startCamera. */}
      {cameraState === 'error' && displayError && (
        <div id={`${id}-error-overlay`} className="barcode-capture-composer__error-overlay">
          <p id={`${id}-error-text`} className="barcode-capture-composer__error-text">{displayError}</p>
        </div>
      )}

      {/* Unsupported code: message plus the way back to scanning. */}
      {isUnsupported && (
        <div id={`${id}-unsupported`} className="barcode-capture-composer__unsupported" role="status">
          <p id={`${id}-unsupported-text`} className="barcode-capture-composer__unsupported-text">{unsupportedMessage}</p>
          <HoverGradientButton id={`${id}-scan-again`} onClick={handleScanAgain}>
            {t('scanAgain')}
          </HoverGradientButton>
        </div>
      )}

      {/* Label capture + optional torch while ZXing decode runs. */}
      {cameraState === 'active' && phase === 'searching' && !isUnsupported && (
        <div id={`${id}-controls`} className="barcode-capture-composer__controls">
          <HoverGradientButton
            id={`${id}-scan-label`}
            variant="outline"
            size="sm"
            onClick={() => void captureLabel()}
            disabled={labelScanning}
            className="barcode-capture-composer__scan-button"
          >
            {labelScanning ? t('scanning') : t('scanLabel')}
          </HoverGradientButton>
          {/* Inline torch toggle; the phone fullscreen layout hides it in favour of the top bar. */}
          {hasTorch && (
            <IconButton
              id={`${id}-torch`}
              ariaLabel={t('torchAriaLabel')}
              onClick={() => void toggleTorch()}
              className={`barcode-capture-composer__torch-button ${torchOn ? 'barcode-capture-composer__torch-button--on' : 'barcode-capture-composer__torch-button--off'}`}
            >
              ⚡
            </IconButton>
          )}
        </div>
      )}

      {/* Lookup in flight after a good read. */}
      {framePhase === 'read' && (
        <div id={`${id}-opening`} className="barcode-capture-composer__opening">
          <span id={`${id}-opening-text`}>{t('openingProduct')}</span>
          <span id={`${id}-opening-bar`} className="barcode-capture-composer__opening-bar" />
        </div>
      )}
    </div>
  )
}
